import React, { useEffect, useState } from 'react'

import update from 'react-addons-update'
import { useToasts } from 'react-toast-notifications'
import styled, { useTheme } from 'styled-components'

import { APP_DEFAULT_STATE } from '@api/local'
import { GlobalModalTypeEnum, ModalsPlugin } from '@api/local/ModalsPlugin'
import { MultipleImageContainer, ProductImageInterface } from '@atoms/images/MultipleImageContainer'
import { Button, Heading, Paragraph, Rule, Spacer } from '@atoms/index'
import { useConfig } from '@client/contexts/ConfigProvider'
import { ResponsiveProperty, ResponsivePXValue, SidePagePadding, theme, ZeroSpace } from '@components/Theme'
import { useEvents } from '@contexts/GTMProvider'
import {
  useAddItemToCartMutation,
  useChangeCartItemQuantityMutation, useGetAppQuery, useRemoveItemFromCartMutation, useUserCartQuery, useUserDetailsQuery,
  WineDetailsFragment, WineTagFragment,
} from '@hooks/api'
import { ItemInterfaceNew } from '@lib/GTM'
import { Utilities } from '@lib/Utilities'
import {
  CategoryTag,
  FloatingUpSell,
  WineDiscount,
} from '@molecules/index'
import { DeviceTypeEnum, NumberOfPortionsEnum, Product, ProductAvailabilityEnum, ProductStockStatusEnum } from '@uctypes/api/globalTypes'
import { useOutOfStockNotifier } from '@utility/Commons'

import { LowStockContainer } from '../misc'

import { DishInfoTable, DishInfoTableData } from './DishInfoTable'

const Container = styled.div`
  display: flex;
  flex-direction: column;
`

const ImageContainer = styled.div`
  position: relative;
  overflow: hidden;
  ${ResponsivePXValue('border-radius', { mobile: '0', tablet: '0', desktop: '16px' })}
  ${ResponsivePXValue('height', { mobile: '100vw', tablet: '50vw', desktop: '576px' })}
  ${ResponsivePXValue('width', { mobile: '100vw', tablet: '100vw', desktop: '576px' })}
  ${ResponsivePXValue('min-width', { mobile: '100vw', tablet: '100vw', desktop: '576px' })}
`

const WineContainer = styled.div`
  display: flex;
  ${ResponsiveProperty('flex-direction', { mobile: 'column-reverse', tablet: 'column-reverse', desktop: 'row' })}
  ${ResponsiveProperty('align-items', { mobile: 'stretch', tablet: 'stretch', desktop: 'flex-start' })}
  ${ResponsivePXValue('margin-bottom', { mobile: '22px', tablet: '24px', desktop: '32px' })}
  ${ResponsivePXValue('padding', { mobile: '0', tablet: '0', desktop: `0 ${theme.pagePadding.desktop}px` })}
`

const CategoryContainer = styled.div`
  ${ResponsiveProperty('display', { mobile: 'none', tablet: 'none', desktop: 'flex' })}
`

const ContentContainer = styled.div`
  display: flex;
  flex-grow: 1;
  flex-direction: column;
  justify-content: center;
  ${ResponsiveProperty('padding', { mobile: '24px 16px 0 16px', tablet: '24px 16px 0 16px', desktop: '0 24px 0 0' })}

  .header-title {
    ${ZeroSpace}
    ${ResponsivePXValue('line-height', '40px')}
  }

 .header-subtitle {
    ${ZeroSpace}
    ${ResponsivePXValue('line-height', '24px')}
  }

  .content-text-description {
    ${ZeroSpace}
    ${ResponsivePXValue('line-height', '24px')}
  }
`

const RuleContainer = styled.div`
  ${ResponsivePXValue('margin-bottom', { mobile: '24px', tablet: '28px', desktop: '36px' })}
  ${SidePagePadding}
`

const TagsContainer = styled.div`
  display: flex;
  align-self: stretch;
  overflow: auto;

  ${SidePagePadding}
  ${ResponsiveProperty('justify-content', { mobile: 'space-between', tablet: 'space-between', desktop: 'space-around' })};

  .tag {
    justify-content: flex-start;
    ${ResponsivePXValue('min-width', '116px')}

    img {
      ${ResponsivePXValue('height', '24px')}
      ${ResponsivePXValue('width', '24px')}
      ${ResponsivePXValue('min-width', '24px')}
    }
  }
`

const CostContainer = styled.div`
  .rand {
    ${ZeroSpace}
    line-height: 1;
  }
`

const WineInfoTableContainer = styled.div`
  ${ResponsivePXValue('margin-bottom', { mobile: '16px', tablet: '20px', desktop: '24px' })}
`

export interface WineBottleDetailsProps {
  wine: WineDetailsFragment
}

interface WineBottleDetailsState {
  quantity: number
  pairingsOpen: boolean
  loading: boolean
  outOfStock: boolean
  currentAmountInCart: number
  portionSize: NumberOfPortionsEnum
}

const DEFAULT_STATE: WineBottleDetailsState = {
  quantity: 1,
  pairingsOpen: false,
  loading: false,
  outOfStock: false,
  currentAmountInCart: null,
  portionSize: NumberOfPortionsEnum.SERVES_2,
}

export const WineBottleDetails = React.memo(({ wine }: WineBottleDetailsProps): JSX.Element => {

  const config = useConfig()
  const [state, setState] = useState<WineBottleDetailsState>({ ...DEFAULT_STATE })
  const [addItemToCart] = useAddItemToCartMutation()
  const { data: userDetailsData } = useUserDetailsQuery({ ssr: config.fetchSSRQuery() })
  const theme = useTheme()
  const events = useEvents()
  const { addToast } = useToasts()
  const wineImages = wine?.productImages as unknown as ProductImageInterface[]
  const { data: userCartData } = useUserCartQuery({ ssr: config.fetchSSRQuery() })
  const isOutOfStockItem = useOutOfStockNotifier()
  const checkoutStatus = userCartData?.currentUser?.checkoutStatus
  const [changeCartItemQuantity] = useChangeCartItemQuantityMutation()
  const [removeItemFromCart] = useRemoveItemFromCartMutation()
  const { data: appData = { app: { ...APP_DEFAULT_STATE } } } = useGetAppQuery()
  const isMobile = appData.app.deviceType === DeviceTypeEnum.MOBILE

  const cartItems = userCartData?.currentUser?.activeCart?.cartItems

  const dishInfoItems = [
    { key: 'Variety', value: wine?.wineCategory?.title },
    ...(wine?.vintage != null ? [{ key: 'Vintage', value: `${wine.vintage}` }] : []),
    ...(wine?.sugar != null ? [{ key: 'Sugar', value: `${wine.sugar}g` }] : []),
    ...(wine?.alcoholContent != null ? [{ key: 'Alcohol', value: `${wine.alcoholContent}%` }] : []),
  ].filter((item): item is { key: string; value: string } => item.value != null)

  const dishInfoTableData: DishInfoTableData[] = []
  for (let i = 0; i < dishInfoItems.length; i += 2) {
    dishInfoTableData.push({
      col1: dishInfoItems[i],
      col2: dishInfoItems[i + 1] || { key: '', value: '' },
    })
  }

  const _toggleLoading = (loading: boolean) => {
    setState((prevState) => update(prevState, {
      loading: { $set: loading },
    }))
  }

  const _handleAddToCart = async (): Promise<void> => {
    if (!_handleDefaultAddressCheck() || !_handleAvailabilityCheck() || state.quantity === 0) return
    try {

      if (isOutOfStockItem(wine as unknown as Product, wine.quantityInCart, checkoutStatus.subscriptionIsSaved)) {
        return
      }

      _toggleLoading(true)

      await addItemToCart({
        variables: { productId: wine?.id, quantity: state.quantity },
      })
      const logData = {
        itemName: wine?.name,
        itemId: wine?.id,
        itemGroupId: wine?.id,
        price: wine?.price,
        itemBrand: wine?.vineyard?.name,
        itemCategory: 'Wines',
        itemVariant: wine?.wineCategory?.title,
        itemListVariant: 'Wines',
        quantity: state.quantity,
        itemImage: wine?.coverImage?.location,
        itemStockCount: 1,
        itemChef: wine?.vineyard?.name,
        isMealkit: 'no',
        itemServingSize: 1,
      }

      const snakedData = Utilities.toSnakeCase(logData) as unknown as ItemInterfaceNew
      events.hasAddedToCart(snakedData)

      addToast(`Successfully added (${state.quantity}) item to cart.`, {
        appearance: 'success',
        autoDismiss: true,
      })
    } catch (e) {
      addToast(e.message, {
        appearance: 'error',
        autoDismiss: true,
      })
    }
    _toggleLoading(false)
  }

  const _handleDefaultAddressCheck = (): boolean => {
    if (!userDetailsData?.currentUser?.hasDefaultAddress) {
      ModalsPlugin.shared().toggleGlobalModal(true, GlobalModalTypeEnum.ADD_LOCATION)
      return false
    } else {
      return true
    }
  }

  const _handleAvailabilityCheck = (): boolean => {
    if (wine.availability !== ProductAvailabilityEnum.AVAILABLE) {
      ModalsPlugin.shared().toggleGlobalModal(true, GlobalModalTypeEnum.DELIVERY_UNAVAILABLE)
      return false
    } else {
      return true
    }
  }

  const isOutOfStock = (): boolean => {
    return wine.stockStatus === ProductStockStatusEnum.OUT_OF_STOCK || wine.stockCount < 1
  }

  useEffect(() => {
    if (wine) {
      setState((prevState) => update(prevState, {
        outOfStock: { $set: isOutOfStock() },
      }))
    }
  }, [wine])

  let tag: WineTagFragment

  const _handleOnCenterClick = async (): Promise<void> => {
    if (!(state.currentAmountInCart > 0)) {
      _handleAddToCart()
    }
  }

  const _handleOnRemove = async (): Promise<void> => {

    const noDefaultAddress = !userDetailsData?.currentUser?.hasDefaultAddress

    setState((prevState) => update(prevState, { addToCartInterrupted: { $set: noDefaultAddress } }))

    if (noDefaultAddress) {
      ModalsPlugin.shared().toggleGlobalModal(true, GlobalModalTypeEnum.ADD_LOCATION)
    } else {

      try {
        setState((prevState) => update(prevState, { loading: { $set: true } }))

        if (state.currentAmountInCart === 1) {

          await removeItemFromCart({
            variables: {
              productId: wine.id,
            },
          })
          setState((prevState) => update(prevState, { currentAmountInCart: { $set: 0 } }))
        } else {
          await changeCartItemQuantity({
            variables: {
              productId: wine.id,
              quantity: state.currentAmountInCart - 1,
            },
          })
        }
        const logData = {
          itemName: wine?.name,
          itemId: wine?.id,
          price: wine?.price,
          itemBrand: wine?.vineyard?.name,
          itemCategory: 'Wines',
          itemVariant: wine?.wineCategory?.title,
          itemListVariant: 'Wines',
          quantity: state.quantity,
          itemImage: wine?.coverImage?.location,
          itemStockCount: 1,
          itemChef: wine?.vineyard?.name,
          isMealkit: 'no',
          itemServingSize: 1,
        }

        const snakedData = Utilities.toSnakeCase(logData) as unknown as ItemInterfaceNew
        events.hasRemovedFromCart(snakedData)

        addToast('Item successfully removed from cart', {
          appearance: 'success',
          autoDismiss: true,
        })
      } catch (e) {
        addToast(e.message, {
          appearance: 'error',
          autoDismiss: true,
        })
      }
      setState((prevState) => update(prevState, { loading: { $set: false } }))

    }
  }

  const _handleOnAdd = (): void => {
    _handleAddToCart()
  }

  const isCardItemInBasket = () => {

    const quantity = cartItems?.find((item) => item?.product?.id === wine?.id)?.quantity || 0

    setState((prevState) => update(prevState, {
      currentAmountInCart: { $set: quantity },
    }))
  }

  useEffect(() => {
    isCardItemInBasket()
  }, [userCartData])

  const category = Utilities.getCategoryFromMeal(wine)
  // Hierarchical text resolution: item-level takes precedence over category-level
  const upSellTexts = Utilities.getHierarchicalText(wine?.upSellText, category?.upSellText)
  let upSellText: string

  return (
    <Container>
      <WineDiscount />
      <WineContainer>
        <ContentContainer>
          <CategoryContainer>
            <CategoryTag image={wine?.wineCategory?.icon?.desktop} title={wine?.wineCategory?.title} />
          </CategoryContainer>
          <Spacer universal='16px' />
          <Heading variant='h3' color={theme.colors.greys.liteCodGrey} className='header-title'>
            {wine?.name}
          </Heading>
          <Spacer universal='8px' />
          <Paragraph variant='p1' color={theme.colors.greys.darkCodGrey} className='content-text-description'>
            {wine?.description}
          </Paragraph>
          <Spacer universal='16px' />
          <LowStockContainer
            displayRule
            product={wine}
            flexDirection={isMobile ? 'row' : 'column'}
            alignItems={isMobile ? 'center' : 'flex-start'}>
            <CostContainer>
              <Heading className='rand' variant='h3' color={theme.colors.greys.liteCodGrey}>
                {`R${wine?.price}`}
              </Heading>
            </CostContainer>
          </LowStockContainer>
          <Spacer universal='16px' />
          <Button
            className='add-button'
            title={state.outOfStock ? 'OUT OF STOCK' : 'ADD TO CART'}
            color='black'
            fullWidth
            loading={state.loading}
            disabled={state.loading || state.outOfStock}
            amount={state.currentAmountInCart}
            onClick={_handleOnCenterClick}
            onLeftIconClick={_handleOnRemove}
            onRightIconClick={_handleOnAdd} />
          <Spacer universal='24px' />
          <WineInfoTableContainer>
            <DishInfoTable data={dishInfoTableData} />
          </WineInfoTableContainer>
        </ContentContainer>
        <ImageContainer>
          <If condition={upSellTexts?.length > 0}>
            <For each='upSellText' of={upSellTexts}>
              <FloatingUpSell text={upSellText} />
            </For>
          </If>
          <MultipleImageContainer images={wineImages}/>
        </ImageContainer>
      </WineContainer>
      <RuleContainer>
        <Rule color='slate' />
      </RuleContainer>
      <TagsContainer>
        <For each='tag' of={wine?.tags || []}>
          <CategoryTag
            image={tag.icon.desktop}
            title={tag?.title} key={tag.id}
            showBackground={false}
            className='tag' />
        </For>
      </TagsContainer>
    </Container>
  )
})

WineBottleDetails.displayName = 'WineBottleDetails'
